import axios from "axios";
import { skeletonStoreSelectors } from "../store/index";
import { RequestOptions, Route } from "./apiTypes";
import { ToasterService } from "../services/toaster/toaster.service";
import errors from "./apiErrors";

const baseURL: string = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/wave';

const _log = (...msgs: any[]) => {
    console.info("[Base API]", ...msgs);
};

const _getCookie = (name: string): string | void => {
    const match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)")
    );

    if (match) {
        return match[2];
    }
};

const _get = (relativeURL: string, data?: any, headers?: any): Promise<any> => {
    let url = relativeURL;
    if (data) {
        url = url + "?";
        Object.keys(data).map((key, index) => {
            if (index == Object.keys(data).length - 1) {
                url = url + `${key}=${data[key]}`;
            } else {
                url = url + `${key}=${data[key]}&`;
            }
        });
    }
    return axios.get(baseURL + url, {
        headers
    });
};

const _post = (relativeURL: string, data: any, headers?: any): Promise<any> => {
    return axios.post(baseURL + relativeURL, data, {
        headers
    });
};

const _patch = (
    relativeURL: string,
    data: any,
    headers?: any
): Promise<any> => {
    return axios.patch(baseURL + relativeURL, data, {
        headers
    });
};

const _put = (relativeURL: string, data: any, headers?: any): Promise<any> => {
    return axios.put(baseURL + relativeURL, data, {
        headers
    });
};

const _delete = (
    relativeURL: string,
    data?: any,
    headers?: any
): Promise<any> => {
    return axios.delete(baseURL + relativeURL, {
        headers
    });
};
/*
const _getRoutes = async (): Promise<Route[]> => {
    const routes: Route[] = await (_get(
        "/routes/public/",
        null,
        _getHeaders()
    ) as any);
    return routes;
};

 */

const _odata = (
    relativeURL: string,
    options: any,
    headers?: any
): Promise<any> => {
    // create query string from ODATA options
    const _ODataQuery = {
        $skip: options.skip,
        $top: options.top
    };

    if (options.orderby) {
        _ODataQuery["$orderby"] = options.orderby;
    }
    if (options.groupby) {
        _ODataQuery["$groupby"] = options.groupby;
    }
    if (options.search) {
        _ODataQuery["$search"] = options.search;
    }
    if (options.select) {
        _ODataQuery["$select"] = options.select.join(",");
    }
    if (options.filter) {
        _ODataQuery["$filter"] = options.filter;
    }

    return axios.get(baseURL + relativeURL, {
        params: _ODataQuery,
        headers
    });
};

const _getLoginStatus = (): { loggedIn: boolean; token?: string } => {
    return {
        loggedIn: !!localStorage.getItem("sessionid"),
        token: localStorage.getItem("sessionid")
    };
};

const _getHeaders = (): any => {
    let headers = {};

    const token = _getLoginStatus().token;
    if (token) {
        headers["Authorization"] = "Bearer " + token;
    }

    return headers;
};

const _request = ({
    // Route ID
    routeID,
    // Body parameters
    parameters = null,
    // Route parameters
    routeParameters = null,
    // OData query parameters
    odataParameters = undefined,
    // whether to use formdata for request body
    formData = true
}: RequestOptions): Promise<any> => {
    // get route information
    const route = skeletonStoreSelectors.apiRoutes()[routeID];

    _log("Request", {
        routeID,
        parameters,
        routeParameters,
        odataParameters,
        route
    });

    if (!route || !route.path || !route.extra) {
        throw Error("Route unavailable");
    }

    // odata route is identified by a boolean in extra.odata property
    if (route.extra && route.extra.odata) {
        route.method = "ODATA";
    }

    // map request types to request calls
    const methods = {
        POST: _post,
        GET: _get,
        PUT: _put,
        PATCH: _patch,
        DELETE: _delete,
        ODATA: _odata
    };

    // replace any route parameters
    const routePath = route.path.replace(/:(\w+):/gi, (all, code) => {
        if (!routeParameters[code]) {
            throw new Error("Missing route parameter");
        } else {
            return routeParameters[code];
        }
    });

    if (typeof methods[route.method] !== "function") {
        throw Error(
            `Route method not recognized: ${route.method} [${routeID}]`
        );
    }

    let body = null;

    if (route.extra.odata) body = odataParameters;
    else if (parameters && formData) {
        body = new FormData();

        const paramNames = Object.keys(parameters);
        for (let i = 0; i < paramNames.length; i++) {
            const param = paramNames[i];

            body.append(param, parameters[param]);
        }
    } else if (parameters) {
        body = { ...parameters };
    }

    // execute request
    return methods[route.method](routePath, body, _getHeaders());
};

const _handleError = (response, msg = undefined) => {
    _log("Handling error\n", response);

    if (!response || response == undefined) {
        if (msg) {
            ToasterService.errorToast(msg);
        }

        return;
    }

    if (
        response.status === 401 ||
        (response.status === 500 &&
            [9010, 9011, 9012, 9020, 9021].includes(response.data.code)) ||
        response.status == 403
    ) {
        localStorage.removeItem("sessionid");
        window.location.reload();
    } else {
        const isValidStatus =
            response.status === 401 ||
            (response.status === 500 && response.data.code === 9010) ||
            response.status == 403;
        if (!isValidStatus && errors.hasOwnProperty(response.status)) {
            ToasterService.errorToast(errors[response.status]);
        } else if (
            !isValidStatus &&
            errors.hasOwnProperty(response.data.code)
        ) {
            ToasterService.errorToast(errors[response.data.code]);
        } else if (msg) {
            ToasterService.errorToast(msg);
        }
    }
};

const _handleMessage = (response, msg = undefined) => {
    if (!response) {
        if (msg) {
            ToasterService.errorToast(msg);
        }
        return;
    } else {
        const isValidStatus =
            response.status === 401 ||
            (response.status === 500 && response.data.code === 9010) ||
            response.status == 403;
        if (!isValidStatus && errors.hasOwnProperty(response.status)) {
            ToasterService.errorToast(errors[response.status]);
        } else if (msg) {
            ToasterService.errorToast(msg);
        }
    }
};

export const API = {
    getCookie: _getCookie,
    //getRoutes: _getRoutes,
    getHeaders: _getHeaders,

    get: _get,
    post: _post,
    put: _put,
    patch: _patch,
    delete: _delete,
    odata: _odata,
    request: _request,

    getLoginStatus: _getLoginStatus,

    handleError: _handleError,

    handleMessage: _handleMessage,

    baseURL
};
